/**
 * Banyomda / Predictive Search — hardened runtime v5.22
 *
 * Contract:
 * - Native Shopify Predictive Search endpoint + dedicated section.
 * - Locale-aware route from Liquid `routes.predictive_search_url`.
 * - One in-flight request per component; stale responses are rejected.
 * - HTML section response is the primary rendering path.
 * - JSON predictive-search response is a defensive fallback when the section
 *   response is malformed or the server returns a Liquid rendering error.
 * - No minimum-character gate: Shopify predictive search accepts partial terms.
 * - Component-local DOM replacement; no dependency on page-level reinitialization.
 */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.cachedMap = new Map();
      this.cacheTtl = 5 * 60 * 1000;
      this.cacheMaxEntries = 20;
      this.abortController = null;
      this.requestToken = 0;
      this.activeUrl = '';
      this.lastQuery = '';
      this.fallbackController = null;
      this.fallbackToken = 0;

      this.handleInput = FoxTheme.utils.debounce(this.onChange.bind(this), 250);
      this.handleFocus = this.onFocus.bind(this);
      this.handleReset = this.clear.bind(this);
      this.handleProductTypeChange = this.handleProductTypeChange.bind(this);
      this.boundHandleClickOutside = this.handleClickOutside.bind(this);
      this.boundMenuDrawerOpen = () => this.toggleSearchState(false);

      this.searchContent = this.querySelector('.search__content');
      this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
      this.header = document.querySelector('header');
      this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');
      this.isHeaderSearch = this.closest('header') !== null;

      this.states = {
        OPEN: 'predictive-search-open',
        LOADING: 'btn--loading',
        SEARCH_OPEN: 'search-open',
      };

      this.input?.addEventListener('input', this.handleInput);
      this.input?.addEventListener('focus', this.handleFocus);
      this.resetButton?.addEventListener('click', this.handleReset);
      document.addEventListener('click', this.boundHandleClickOutside);
      document.addEventListener('menu-drawer:open', this.boundMenuDrawerOpen);
      this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange);
    }

    disconnectedCallback() {
      this.requestToken += 1;
      this.abortController?.abort();
      this.abortController = null;
      this.fallbackToken += 1;
      this.fallbackController?.abort();
      this.fallbackController = null;
      this.input?.removeEventListener('input', this.handleInput);
      this.input?.removeEventListener('focus', this.handleFocus);
      this.resetButton?.removeEventListener('click', this.handleReset);
      document.removeEventListener('click', this.boundHandleClickOutside);
      document.removeEventListener('menu-drawer:open', this.boundMenuDrawerOpen);
      this.searchProductTypes?.removeEventListener('change', this.handleProductTypeChange);
    }

    get input() {
      return this.querySelector('input[type="search"]');
    }

    get resetButton() {
      return this.querySelector('button[type="reset"]');
    }

    get searchProductTypes() {
      return this.querySelector('#SearchProductTypes');
    }

    get resultsTarget() {
      return this.querySelector('[data-predictive-results]');
    }

    getQuery() {
      return this.input?.value.trim() || '';
    }

    onFocus() {
      this.updateViewportMetrics();

      if (!this.searchRecommendationEmpty) {
        this.searchContent?.classList.remove('hidden');
      }

      const query = this.getQuery();
      if (!query) {
        this.toggleSearchState(true);
        if (this.searchRecommendationEmpty) this.searchContent?.classList.add('hidden');
        return;
      }

      this.toggleSearchState(true);
      this.renderSection(this.setupURL().toString());
    }

    updateViewportMetrics() {
      if (!this.header) return;
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${Math.round(this.header.getBoundingClientRect().bottom)}px`
      );
    }

    clear(event = null) {
      event?.preventDefault();
      this.requestToken += 1;
      this.abortController?.abort();
      this.abortController = null;
      this.activeUrl = '';
      this.lastQuery = '';
      this.fallbackToken += 1;
      this.fallbackController?.abort();
      this.fallbackController = null;

      if (this.input) {
        this.input.value = '';
        this.input.focus();
      }

      if (this.resultsTarget) this.resultsTarget.innerHTML = '';
      this.removeAttribute('results');
      this.removeAttribute('error');
      this.setLoadingState(false);
      this.toggleSearchState(false);
    }

    handleProductTypeChange() {
      if (this.getQuery()) this.renderSection(this.setupURL().toString());
    }

    setupURL() {
      const predictiveSearchRoute = FoxTheme?.routes?.predictive_search_url;
      if (!predictiveSearchRoute) {
        throw new Error('[Banyomda] Shopify predictive search route is unavailable.');
      }

      const url = new URL(predictiveSearchRoute, document.baseURI);
      const searchTerm = this.getQuery();
      const productType = this.searchProductTypes?.value?.trim() || '';
      const query = productType ? `product_type:${productType} AND ${searchTerm}` : searchTerm;

      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection');
      url.searchParams.set('resources[limit]', String(Math.min(Number(this.dataset.resultsLimit) || 6, 6)));
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
      url.searchParams.set('section_id', 'predictive-search');
      return url;
    }

    getJsonFallbackURL(url) {
      const fallback = new URL(url.toString());
      if (!fallback.pathname.endsWith('.json')) fallback.pathname += '.json';
      fallback.searchParams.delete('section_id');
      return fallback;
    }

    onChange() {
      const query = this.getQuery();
      if (!query) {
        this.clear();
        return;
      }

      this.renderSection(this.setupURL().toString());
    }

    renderSection(url) {
      const requestToken = ++this.requestToken;
      this.abortController?.abort();
      this.fallbackToken += 1;
      this.fallbackController?.abort();
      this.fallbackController = null;
      this.removeAttribute('error');

      if (this.activeUrl === url && this.hasAttribute('results')) return;

      this.activeUrl = url;
      this.lastQuery = this.getQuery();

      const cached = this.getCached(url);
      if (cached) {
        this.renderSearchResults(cached, requestToken);
        return;
      }

      this.renderSectionFromFetch(url, requestToken);
    }

    getCached(url) {
      const entry = this.cachedMap.get(url);
      if (!entry) return null;
      if (Date.now() - entry.timestamp > this.cacheTtl) {
        this.cachedMap.delete(url);
        return null;
      }
      this.cachedMap.delete(url);
      this.cachedMap.set(url, entry);
      return entry.text;
    }

    setCached(url, text) {
      this.cachedMap.delete(url);
      this.cachedMap.set(url, { text, timestamp: Date.now() });
      while (this.cachedMap.size > this.cacheMaxEntries) {
        this.cachedMap.delete(this.cachedMap.keys().next().value);
      }
    }

    async renderSectionFromFetch(url, requestToken) {
      this.setLoadingState(true);
      const controller = new AbortController();
      this.abortController = controller;

      try {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Accept: 'text/html' },
          credentials: 'same-origin',
        });
        const responseText = await response.text();

        if (!response.ok) throw new Error(`Predictive search ${response.status}`);
        if (requestToken !== this.requestToken) return;

        const sectionValid = this.isValidSectionResponse(responseText);
        const serverError = /\bLiquid error\b|\bInternal Server Error\b/i.test(responseText);

        if (!sectionValid || serverError) {
          try {
            await this.renderJsonFallback(url, requestToken);
          } catch (fallbackError) {
            if (fallbackError?.name !== 'AbortError' && requestToken === this.requestToken) {
              this.setAttribute('error', 'Predictive search failed');
              this.removeAttribute('results');
            }
          }
          return;
        }

        this.setCached(url, responseText);
        this.renderSearchResults(responseText, requestToken);
      } catch (error) {
        if (error?.name === 'AbortError' || requestToken !== this.requestToken) return;
        console.error('[Banyomda] Predictive search request failed.', error);
        try {
          await this.renderJsonFallback(url, requestToken);
        } catch (fallbackError) {
          if (fallbackError?.name !== 'AbortError' && requestToken === this.requestToken) {
            console.error('[Banyomda] Predictive search fallback failed.', fallbackError);
            this.setAttribute('error', 'Predictive search failed');
            this.removeAttribute('results');
          }
        }
      } finally {
        if (requestToken === this.requestToken && this.abortController === controller) {
          this.abortController = null;
          this.setLoadingState(false);
        }
      }
    }

    isValidSectionResponse(responseText) {
      const parsedDoc = new DOMParser().parseFromString(responseText, 'text/html');
      const sectionElement = parsedDoc.querySelector('#shopify-section-predictive-search');
      const contentElement = sectionElement?.querySelector('[data-predictive-results]') || parsedDoc.querySelector('[data-predictive-results]');
      return Boolean(contentElement);
    }

    async renderJsonFallback(url, requestToken) {
      this.fallbackController?.abort();

      const controller = new AbortController();
      const fallbackToken = ++this.fallbackToken;
      this.fallbackController = controller;

      try {
        const fallbackUrl = this.getJsonFallbackURL(url);
        const response = await fetch(fallbackUrl, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`Predictive JSON ${response.status}`);

        const data = await response.json();
        if (requestToken !== this.requestToken || fallbackToken !== this.fallbackToken) return;

        const resources = data?.resources || {};
        this.renderFallbackResults(resources, requestToken);
      } finally {
        if (fallbackToken === this.fallbackToken && this.fallbackController === controller) {
          this.fallbackController = null;
        }
      }
    }

    renderSearchResults(responseText, requestToken) {
      if (requestToken !== this.requestToken) return;

      const targetElement = this.resultsTarget;
      if (!targetElement) {
        this.setAttribute('error', 'Predictive search target not found');
        return;
      }

      const parsedDoc = new DOMParser().parseFromString(responseText, 'text/html');
      const sectionElement = parsedDoc.querySelector('#shopify-section-predictive-search');
      const contentElement = sectionElement?.querySelector('[data-predictive-results]') || parsedDoc.querySelector('[data-predictive-results]');

      if (!contentElement) {
        this.setAttribute('error', 'Invalid predictive search response');
        this.removeAttribute('results');
        return;
      }

      targetElement.innerHTML = contentElement.innerHTML;
      this.renderSearchIntentSuggestion(targetElement);
      targetElement.setAttribute('aria-busy', 'false');
      this.removeAttribute('error');
      this.setAttribute('results', 'true');
      this.searchContent?.classList.remove('hidden');
      this.classList.add(this.states.OPEN);
      this.input?.setAttribute('aria-expanded', 'true');

      if (this.isHeaderSearch) {
        document.body.classList.add(this.states.SEARCH_OPEN);
        this.toggleNavButtonVisibility(false);
      }
    }

    renderFallbackResults(resources, requestToken) {
      if (requestToken !== this.requestToken) return;
      const target = this.resultsTarget;
      if (!target) return;

      const products = Array.isArray(resources.products) ? resources.products : [];
      const collections = Array.isArray(resources.collections) ? resources.collections : [];
      const articles = Array.isArray(resources.articles) ? resources.articles : [];
      const pages = Array.isArray(resources.pages) ? resources.pages : [];

      const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
      }[char]));

      const productMarkup = products.map((product) => {
        const image = product.image?.url || product.featured_image?.url || '';
        const imageMarkup = image
          ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" width="160" height="160">`
          : '';
        return `<a class="byd-predictive-fallback__product" href="${escapeHtml(product.url || '#')}">${imageMarkup}<span><small>${escapeHtml(product.vendor || '')}</small><strong>${escapeHtml(product.title || '')}</strong></span></a>`;
      }).join('');

      const collectionMarkup = collections.map((collection) =>
        `<a class="byd-predictive-fallback__link" href="${escapeHtml(collection.url || '#')}">${escapeHtml(collection.title || '')}</a>`
      ).join('');

      const content = [
        productMarkup ? `<section><h4>Ürünler</h4><div class="byd-predictive-fallback__products">${productMarkup}</div></section>` : '',
        collectionMarkup ? `<section><h4>Kategoriler</h4><div class="byd-predictive-fallback__links">${collectionMarkup}</div></section>` : '',
        articles.length || pages.length ? `<section><h4>İçerikler</h4><div class="byd-predictive-fallback__links">${[...articles, ...pages].map((item) => `<a class="byd-predictive-fallback__link" href="${escapeHtml(item.url || '#')}">${escapeHtml(item.title || '')}</a>`).join('')}</div></section>` : '',
      ].filter(Boolean).join('');

      target.innerHTML = content || `<div class="empty-state"><p>Sonuç bulunamadı.</p></div>`;
      this.renderSearchIntentSuggestion(target);
      this.searchContent?.classList.remove('hidden');
      this.classList.add(this.states.OPEN);
      this.setAttribute('results', 'true');
      this.removeAttribute('error');
      this.input?.setAttribute('aria-expanded', 'true');

      if (this.isHeaderSearch) {
        document.body.classList.add(this.states.SEARCH_OPEN);
        this.toggleNavButtonVisibility(false);
      }
    }

    renderSearchIntentSuggestion(targetElement) {
      const query = this.getQuery();
      const route = window.BanyomdaSearchIntent?.findRoute?.(query);
      if (!route || !targetElement) return;

      targetElement.querySelector('[data-search-intent-suggestion]')?.remove();
      const wrapper = document.createElement('div');
      wrapper.setAttribute('data-search-intent-suggestion', '');
      wrapper.className = 'byd-search-intent';
      const link = document.createElement('a');
      link.className = 'byd-search-intent__link';
      link.href = route.url;
      link.innerHTML = `<span>${route.label}</span><span aria-hidden="true">→</span>`;
      const label = document.createElement('div');
      label.className = 'byd-search-intent__label';
      label.textContent = 'Kategoriye git';
      wrapper.append(label, link);
      targetElement.prepend(wrapper);
    }

    handleClickOutside(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const shouldClose = this.isHeaderSearch
        ? !this.contains(target) &&
          ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) || target.classList.contains('header__search-close'))
        : !this.contains(target);

      if (shouldClose) setTimeout(() => this.toggleSearchState(false));
    }

    toggleSearchState(isOpen) {
      this.classList.toggle(this.states.OPEN, isOpen);
      this.input?.setAttribute('aria-expanded', String(isOpen));
      if (this.isHeaderSearch) {
        document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
        this.toggleNavButtonVisibility(!isOpen);
      }
      if (!isOpen && this.searchRecommendationEmpty) this.searchContent?.classList.add('hidden');
    }

    toggleNavButtonVisibility(isOpen) {
      this.toggleNavigationButton?.classList.toggle('is-show', isOpen);
    }

    setLoadingState(isLoading) {
      if (isLoading) {
        this.setAttribute('loading', 'true');
        this.resetButton?.classList.add(this.states.LOADING);
      } else {
        this.removeAttribute('loading');
        this.resetButton?.classList.remove(this.states.LOADING);
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
